/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {
    Flex,
    Box,
    HStack,
    useStyleConfig,
    Popover,
    PopoverTrigger,
    IconButton,
    Portal,
    PopoverContent,
    PopoverArrow,
    PopoverBody,
    PopoverCloseButton,
    SkeletonText
} from '@chakra-ui/react'
import {noop} from '../../utils/utils'
import {InfoIconFilled} from '../icons'

/**
 * SwatchGroup allows you to create a list of swatches
 * Each Swatch is a link with will direct to a href passed to them
 */
const SwatchGroup = (props) => {
    const {
        loading,
        spacing,
        showLabel,
        displayName,
        children,
        value,
        label = '',
        variant,
        onChange = noop,
        size
    } = props
    const styles = useStyleConfig('SwatchGroup')

    return (
        <Flex {...styles.swatchGroup} role="radiogroup">
            {showLabel && (
                <HStack {...styles.swatchLabel}>
                    {props.tooltip && (
                        <Popover
                            placement="top"
                            preventOverflow={true}
                            variant="chipTooltip"
                            arrowSize={12}
                            arrowShadowColor="none"
                            closeOnEsc={true}
                            gutter={16}
                        >
                            <PopoverTrigger>
                                <IconButton
                                    minWidth="unset"
                                    height="unset"
                                    minHeight="unset"
                                    lineHeight={0}
                                    icon={<InfoIconFilled boxSize={3} color="stokkeCore.black" />}
                                    variant="unstyled"
                                />
                            </PopoverTrigger>
                            {/* By default the Popover component is not rendered in portal */}
                            <Portal>
                                {/*
                            Popover component does not respect passed z-index props.
                            For this reason is used additional Box component.
                        */}
                                <Box zIndex="1301" w="full" h="full" position="relative">
                                    <PopoverContent>
                                        <PopoverArrow />
                                        <PopoverBody>
                                            <PopoverCloseButton />
                                            <Box
                                                overflowY="auto"
                                                display={{base: '-webkit-box', sm: 'block'}}
                                                sx={{
                                                    WebkitBoxOrient: 'vertical',
                                                    WebkitLineClamp: '9'
                                                }}
                                                dangerouslySetInnerHTML={{__html: props.tooltip}}
                                            />
                                        </PopoverBody>
                                    </PopoverContent>
                                </Box>
                            </Portal>
                        </Popover>
                    )}
                    {displayName ? (
                        <>
                            <Box fontWeight="semibold">{`${label}:`}</Box>
                            <SkeletonText
                                isLoaded={!loading}
                                noOfLines={1}
                                width={loading && '10em'}
                            >
                                {displayName}
                            </SkeletonText>
                        </>
                    ) : (
                        <Box fontWeight="semibold" color="stokkeFunctional.alertRed">
                            {label}
                        </Box>
                    )}
                </HStack>
            )}
            <Flex {...styles.swatchesWrapper} className="swatch-group-list" gap={spacing}>
                {React.Children.map(children, (child) => {
                    const childValue = child.props.value

                    return React.cloneElement(child, {
                        selected: childValue === value,
                        variant,
                        size,
                        onChange
                    })
                })}
            </Flex>
        </Flex>
    )
}

SwatchGroup.displayName = 'SwatchGroup'

SwatchGroup.defaultProps = {
    showLabel: true,
    spacing: 2
}

SwatchGroup.propTypes = {
    /**
     * Loading state of the swatch group - renders skeleton instead of the displayName
     */
    loading: PropTypes.bool,
    /**
     * Space between swatches
     */
    spacing: PropTypes.number,
    /**
     * Show & Hide label of the swatch group
     */
    showLabel: PropTypes.bool,
    /**
     * The attribute name of the swatch group (e.g color, size or in case of rendering color groups it would be the label of the group)
     */
    label: PropTypes.string,
    /**
     * The selected Swatch value.
     */
    value: PropTypes.string,
    /**
     * The display value of the selected option. Value is optional and in case of rendering color groups it won't be available
     */
    displayName: PropTypes.string,
    /**
     * The Swatch options to choose between
     */
    children: PropTypes.array,
    /**
     * The shape of the swatches
     */
    variant: PropTypes.oneOf(['square', 'circle', 'chip']),
    /**
     * This function is called when a new option is selected
     */
    onChange: PropTypes.func,
    /**
     * The size of the swatches
     */
    size: PropTypes.string,
    /**
     * The tooltip info button to display for swatch if available
     */
    tooltip: PropTypes.string
}

export default SwatchGroup
